/**
 * Fantasy Console 0 - LocalStorage Utilities
 */

import {
  Cart,
  serializeCart,
  CartValidationError,
  deserializeCart,
} from '@fc0/types'

const STORAGE_KEY_CURRENT_CART = 'fc0_current_cart'
const STORAGE_KEY_PREFERENCES = 'fc0_preferences'
const STORAGE_KEY_CLOUD_SAVE_HASH = 'fc0_cloud_save_hash'

export interface CodeEditorSettings {
  fontSize: 12 | 13 | 14 | 16 | 18
  tabSize: 2 | 4
  lineWrap: boolean
  autoRun: boolean
  autoRunDebounce: number
  vimMode: boolean
}

export interface EditorPreferences {
  lastTab: string
  codeEditor: CodeEditorSettings
  themeId?: string
}

const DEFAULT_CODE_EDITOR_SETTINGS: CodeEditorSettings = {
  fontSize: 13,
  tabSize: 2,
  lineWrap: false,
  autoRun: false,
  autoRunDebounce: 500,
  vimMode: false,
}

const DEFAULT_PREFERENCES: EditorPreferences = {
  lastTab: 'code',
  codeEditor: DEFAULT_CODE_EDITOR_SETTINGS,
}

export { DEFAULT_CODE_EDITOR_SETTINGS }

/**
 * Save the current cart to localStorage
 */
export function saveCurrentCart(cart: Cart): boolean {
  try {
    const json = serializeCart(cart)
    localStorage.setItem(STORAGE_KEY_CURRENT_CART, json)
    return true
  } catch (error) {
    console.error('Failed to save cart to localStorage:', error)
    return false
  }
}

/**
 * Load the current cart from localStorage
 */
export function loadCurrentCart(): Cart | null {
  try {
    const json = localStorage.getItem(STORAGE_KEY_CURRENT_CART)
    if (!json) return null

    return deserializeCart(json)
  } catch (error) {
    console.error('Failed to load cart from localStorage:', error)
    return null
  }
}

/**
 * Clear the current cart from localStorage
 */
export function clearCurrentCart(): void {
  localStorage.removeItem(STORAGE_KEY_CURRENT_CART)
}

/**
 * Save the cloud save hash to localStorage
 */
export function saveCloudSaveHash(hash: string | null): void {
  try {
    if (hash === null) {
      localStorage.removeItem(STORAGE_KEY_CLOUD_SAVE_HASH)
    } else {
      localStorage.setItem(STORAGE_KEY_CLOUD_SAVE_HASH, hash)
    }
  } catch (error) {
    console.error('Failed to save cloud save hash:', error)
  }
}

/**
 * Load the cloud save hash from localStorage
 */
export function loadCloudSaveHash(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY_CLOUD_SAVE_HASH)
  } catch (error) {
    console.error('Failed to load cloud save hash:', error)
    return null
  }
}

/**
 * Save editor preferences
 */
export function savePreferences(preferences: Partial<EditorPreferences>): void {
  try {
    const current = loadPreferences()
    const merged = { ...current, ...preferences }
    localStorage.setItem(STORAGE_KEY_PREFERENCES, JSON.stringify(merged))
  } catch (error) {
    console.error('Failed to save preferences:', error)
  }
}

/**
 * Load editor preferences
 */
export function loadPreferences(): EditorPreferences {
  try {
    const json = localStorage.getItem(STORAGE_KEY_PREFERENCES)
    if (!json) return DEFAULT_PREFERENCES
    const parsed = JSON.parse(json) as Partial<EditorPreferences>
    return {
      ...DEFAULT_PREFERENCES,
      ...parsed,
      codeEditor: {
        ...DEFAULT_CODE_EDITOR_SETTINGS,
        ...(parsed.codeEditor || {}),
      },
    }
  } catch (error) {
    console.error('Failed to load preferences:', error)
    return DEFAULT_PREFERENCES
  }
}

/**
 * Export cart as a downloadable JSON file.
 * Uses the File System Access API (showSaveFilePicker) when available
 * for a proper save dialog experience. Falls back to anchor-click download.
 *
 * Returns true if the user confirmed a save location (File System Access API),
 * or false if the fallback anchor-click method was used.
 */
export async function exportCartAsFile(cart: Cart): Promise<boolean> {
  const json = serializeCart(cart)
  const blob = new Blob([json], { type: 'application/json' })
  const filename = sanitizeFilename(cart.metadata.title) + '.json'

  // Use File System Access API when available (Chrome/Edge)
  if ('showSaveFilePicker' in window) {
    try {
      const handle = await (window as any).showSaveFilePicker({
        suggestedName: filename,
        types: [
          {
            description: 'JSON file',
            accept: { 'application/json': ['.json'] },
          },
        ],
      })
      const writable = await handle.createWritable()
      await writable.write(blob)
      await writable.close()
      return true
    } catch (err: any) {
      // User cancelled the save dialog
      if (err?.name === 'AbortError') {
        return false
      }
      throw err
    }
  }

  // Fallback: anchor-click download (Firefox/Safari)
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
  return false
}

/**
 * Import cart from a JSON file and convert to Cart
 */
export function importCartFromFile(file: File): Promise<Cart> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()

    reader.onload = (e) => {
      try {
        const json = e.target?.result as string
        resolve(deserializeCart(json))
      } catch (error) {
        if (error instanceof CartValidationError) {
          reject(new Error(`Invalid cart file: ${error.message}`))
        } else {
          reject(new Error('Failed to read cart file'))
        }
      }
    }

    reader.onerror = () => {
      reject(new Error('Failed to read file'))
    }

    reader.readAsText(file)
  })
}

/**
 * Sanitize a string for use as a filename
 */
function sanitizeFilename(name: string): string {
  return (
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '') || 'untitled'
  )
}

/**
 * Check if localStorage is available
 */
export function isLocalStorageAvailable(): boolean {
  try {
    const testKey = '__fc0_test__'
    localStorage.setItem(testKey, 'test')
    localStorage.removeItem(testKey)
    return true
  } catch {
    return false
  }
}
